/** @file Select tool: point gizmo, else pick a bone or slot (Shift adds, Ctrl toggles), else a marquee. */
import { HANDLE_PX } from '@pose2d/editor-geometry/handlePx';
import { pickPoint } from '@pose2d/editor-geometry/pickPoint';
import { viewCamera } from '@pose2d/editor-geometry/viewCamera';
import { OVERLAY_STYLE } from '@pose2d/overlay/overlayStyle';
import { pickAt } from '../../panels/viewport/pickAt';
import type { ToolPointer } from '../../panels/viewport/viewportTypes';
import { updateSelection } from '../../stores/updateSelection';
import { marqueeGesture } from './marqueeGesture';
import { pointGesture } from './pointGesture';
import { pointTargets, type PointTarget } from './pointTargets';
import { selectModeOf } from './selectModeOf';

/** Socket points cyan, item handle points in the hull-edge colour (R-E1-15). */
const colorOf = (t: PointTarget): number => (t.kind === 'socket' ? OVERLAY_STYLE.socket : OVERLAY_STYLE.hullEdge);

/** A press on a socket or handle point of the selection drags it; idle draws those points. */
export const selectPointer: ToolPointer = {
  press(ctx, p) {
    const targets = pointTargets(ctx);
    const hit = targets[pickPoint({ coords: targets.flatMap((t) => t.world), stride: 2, offset: 0, count: targets.length }, ctx.camera, p.sx, p.sy, HANDLE_PX.point)];
    if (hit !== undefined) return pointGesture(ctx, hit);
    const pick = pickAt(ctx, p.sx, p.sy);
    if (pick.bone < 0 && pick.slot < 0) return marqueeGesture(ctx, p);
    const bones = pick.bone < 0 ? [] : [ctx.maps.boneIds[pick.bone] ?? ''];
    const slots = pick.slot < 0 ? [] : [ctx.maps.slotIds[pick.slot] ?? ''];
    updateSelection({ bones, slots }, selectModeOf(p));
    return null;
  },
  idle(ctx) {
    const targets = pointTargets(ctx);
    if (targets.length === 0) return null;
    return { handles: [], active: null, lines: [], points: targets.map((t) => ({ at: viewCamera.worldToScreen(ctx.camera, t.world[0], t.world[1], [0, 0]), color: colorOf(t) })) };
  },
};
